import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import type { usePartialCharges } from '../../hooks/usePartialCharges';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';
import type { ToolbarGroup } from './useToolbarOverflow';

/** #1078 -- what the Atom group's controls open: the periodic table, or a small popover. */
export type AtomPanel = 'element' | 'isotope' | 'radical';

const RADICAL_KEYS = ['radicalNone', 'radicalDoublet', 'radicalTriplet', 'radicalThree'] as const;

function formatCharge(value: number): string {
  return value > 0 ? `+${value}` : String(value).replace('-', '−');
}

/**
 * #1078 -- the Atom group: while exactly one atom is selected, its element, formal charge,
 * isotope, radical electrons and partial charge sit on the toolbar right after the pointer
 * modes, so they aren't missed in the right rail (which keeps its full form). With no room on
 * the row the group moves into ⋯ like every other group (`menu`).
 */
export function AtomContextControls({
  doc,
  partialCharges,
  hiddenGroups,
  menu = false,
  onPanel,
  onAction
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly partialCharges: ReturnType<typeof usePartialCharges>;
  readonly hiddenGroups: ReadonlySet<ToolbarGroup>;
  readonly menu?: boolean;
  readonly onPanel: (panel: AtomPanel, anchor: HTMLElement | null) => void;
  /** Menu only: closes ⋯ after a direct action (the charge steps). */
  readonly onAction?: () => void;
}) {
  const { t } = useTranslation();
  const { selectedAtom: atom, singleAtomId, selectedFormalCharge, updateSelectedFormalCharge } = doc;
  if (!atom || !singleAtomId || (menu && !hiddenGroups.has('atom'))) return null;

  const symbol = elementSymbol(atom.atomicNumber);
  const charge = selectedFormalCharge?.formalCharge ?? 0;
  const radicals = atom.radicalElectronCount ?? 0;
  const partial = partialCharges.result?.charges[singleAtomId];
  const computing = partialCharges.state.state === 'loading';
  const itemClass = menu ? 'workspace-more-item workspace-more-action' : 'icon-button';
  const role = menu ? 'menuitem' : undefined;

  const elementLabel = t('atomToolbar.element', { symbol });
  const isotopeLabel = t('editorToolPalette.atom.isotopeMassNumber');
  const radicalLabel = t('editorToolPalette.atom.radicalElectrons');
  const stepCharge = (delta: number) => {
    updateSelectedFormalCharge(charge + delta);
    onAction?.();
  };

  return (
    <div
      role="group"
      aria-label={t('atomToolbar.group')}
      className={menu ? 'workspace-more-group' : 'workspace-action-group atom-context-controls'}
      data-toolbar-group={menu ? undefined : 'atom'}
      data-testid={menu ? undefined : 'atom-toolbar'}
      hidden={!menu && hiddenGroups.has('atom')}
    >
      <button
        type="button"
        role={role}
        className={itemClass}
        aria-label={elementLabel}
        title={elementLabel}
        aria-haspopup="dialog"
        onClick={(event) => onPanel('element', event.currentTarget)}
      >
        <span className="atom-glyph" aria-hidden="true">
          {symbol}
        </span>
        {menu ? <span>{elementLabel}</span> : null}
      </button>
      <button
        type="button"
        role={role}
        className={itemClass}
        aria-label={t('workspace.rail.charge.decrease')}
        title={t('workspace.rail.charge.decreaseLiveHint')}
        onClick={() => stepCharge(-1)}
      >
        <span className="atom-glyph" aria-hidden="true">
          ⊖
        </span>
        {menu ? <span>{t('workspace.rail.charge.decrease')}</span> : null}
      </button>
      {charge !== 0 && !menu ? (
        <output className="atom-charge-value" aria-label={t('workspace.rail.charge.selectedValue')}>
          {formatCharge(charge)}
        </output>
      ) : null}
      <button
        type="button"
        role={role}
        className={itemClass}
        aria-label={t('workspace.rail.charge.increase')}
        title={t('workspace.rail.charge.increaseLiveHint')}
        onClick={() => stepCharge(1)}
      >
        <span className="atom-glyph" aria-hidden="true">
          ⊕
        </span>
        {menu ? <span>{t('workspace.rail.charge.increase')}</span> : null}
      </button>
      <button
        type="button"
        role={role}
        className={`${itemClass} ${atom.isotope ? 'atom-control--set' : ''}`}
        aria-label={atom.isotope ? `${isotopeLabel}: ${atom.isotope}` : isotopeLabel}
        title={atom.isotope ? `${isotopeLabel}: ${atom.isotope}` : isotopeLabel}
        aria-haspopup="dialog"
        onClick={(event) => onPanel('isotope', event.currentTarget)}
      >
        {/* The mass number written where chemists write it: ¹³C. "A" is its symbol while unset. */}
        <span className="atom-glyph" aria-hidden="true">
          <sup>{atom.isotope ?? 'A'}</sup>
          {symbol}
        </span>
        {menu ? <span>{isotopeLabel}</span> : null}
      </button>
      <button
        type="button"
        role={role}
        className={`${itemClass} ${radicals ? 'atom-control--set' : ''}`}
        aria-label={`${radicalLabel}: ${t(`editorToolPalette.atom.${RADICAL_KEYS[radicals] ?? 'radicalNone'}`)}`}
        title={`${radicalLabel}: ${t(`editorToolPalette.atom.${RADICAL_KEYS[radicals] ?? 'radicalNone'}`)}`}
        aria-haspopup="dialog"
        onClick={(event) => onPanel('radical', event.currentTarget)}
      >
        <span className="atom-glyph atom-glyph--radical" aria-hidden="true">
          {'•'.repeat(Math.max(1, radicals))}
        </span>
        {menu ? <span>{radicalLabel}</span> : null}
      </button>
      {partial !== undefined ? (
        menu ? null : (
          <output
            className="atom-partial-charge"
            title={t('editorToolPalette.partialCharge.atomLabel', { value: partial.toFixed(3) })}
          >
            δ {partial.toFixed(3).replace('-', '−')}
          </output>
        )
      ) : (
        <button
          type="button"
          role={role}
          className={itemClass}
          disabled={computing}
          aria-label={t(
            computing ? 'editorToolPalette.partialCharge.computing' : 'editorToolPalette.partialCharge.compute'
          )}
          title={t(computing ? 'editorToolPalette.partialCharge.computing' : 'editorToolPalette.partialCharge.compute')}
          onClick={() => {
            void partialCharges.runCompute();
            onAction?.();
          }}
        >
          <span className="partial-charge-glyph" aria-hidden="true">
            δ±
          </span>
          {menu ? <span>{t('editorToolPalette.partialCharge.compute')}</span> : null}
        </button>
      )}
    </div>
  );
}

/** #1078 -- the Isotope and Radical popovers, anchored to their toolbar button (or to ⋯). */
export function AtomPropertyPopover({
  doc,
  panel,
  anchorRef,
  onClose
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly panel: 'isotope' | 'radical';
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  const anchor = useAnchoredPopover(true, anchorRef, ref, () => 240);
  useDismissableOverlay(true, onClose, anchorRef, ref);
  useFocusTrap(true, ref);
  const atom = doc.selectedAtom;
  const [isotopeText, setIsotopeText] = useState(atom?.isotope ? String(atom.isotope) : '');
  useEffect(() => {
    if (anchor && !focused.current) {
      (
        ref.current?.querySelector<HTMLElement>('input, [aria-checked="true"]') ??
        ref.current?.querySelector<HTMLElement>('button')
      )?.focus();
      focused.current = true;
    }
  }, [anchor]);
  if (!atom) return null;

  const close = () => {
    onClose();
    anchorRef.current?.focus();
  };
  const commitIsotope = () => {
    const value = Number(isotopeText);
    if (isotopeText.trim() === '') doc.updateSelectedIsotope(null);
    else if (Number.isInteger(value) && value >= 1 && value <= 300) doc.updateSelectedIsotope(value);
    close();
  };
  const label = t(
    panel === 'isotope' ? 'editorToolPalette.atom.isotopeMassNumber' : 'editorToolPalette.atom.radicalElectrons'
  );

  return anchor ? (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      className="selection-appearance-popover atom-property-popover"
      data-testid={`atom-${panel}-popover`}
      style={{ top: anchor.top, left: anchor.left }}
    >
      <div className="selection-appearance-heading">
        <strong>{label}</strong>
        <button
          type="button"
          className="icon-button"
          aria-label={t('exportDialog.cancel')}
          title={t('exportDialog.cancel')}
          onClick={close}
        >
          <Icon name="close" />
        </button>
      </div>
      {panel === 'isotope' ? (
        <form
          className="atom-isotope-form"
          onSubmit={(event) => {
            event.preventDefault();
            commitIsotope();
          }}
        >
          <input
            type="number"
            step="1"
            min="1"
            max="300"
            aria-label={label}
            placeholder={t('editorToolPalette.atom.isotopeStandard')}
            value={isotopeText}
            onChange={(event) => setIsotopeText(event.target.value)}
          />
          <button
            type="submit"
            className="icon-button"
            aria-label={t('repeatUnit.apply')}
            title={t('repeatUnit.apply')}
          >
            <Icon name="check" />
          </button>
        </form>
      ) : (
        <div className="atom-radical-options" role="radiogroup" aria-label={label}>
          {RADICAL_KEYS.map((key, count) => {
            const checked = (atom.radicalElectronCount ?? 0) === count;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={checked}
                className={checked ? 'active' : undefined}
                onClick={() => {
                  doc.updateSelectedRadicalElectronCount(count === 0 ? null : count);
                  close();
                }}
              >
                {t(`editorToolPalette.atom.${key}`)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  ) : null;
}
